import { useState, useEffect, Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import EmailVerificationBanner from './components/EmailVerificationBanner';

// Lazy load components for better performance (code splitting)
const LandingPage = lazy(() => import('./components/LandingPage'));
const AIScanner = lazy(() => import('./components/AIScanner'));
const ScannerHome = lazy(() => import('./components/ScannerHome'));
const AIStrategyBuilder = lazy(() => import('./components/EnhancedStrategyBuilder'));
const Auth = lazy(() => import('./components/Auth'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail'));
const MarketCommandCenter = lazy(() => import('./components/MarketCommandCenter'));
const SavedStrategies = lazy(() => import('./components/SavedStrategies'));
const GemsTrading = lazy(() => import('./components/GemsTrading'));
const GemsIndiaTrading = lazy(() => import('./components/GemsIndiaTrading'));
const NirvanaTrading = lazy(() => import('./components/NirvanaTrading'));
const WheelStrategy = lazy(() => import('./components/WheelStrategy'));
const AITrimbakPage = lazy(() => import('./components/AITrimbakPage'));
const AIKaliPage = lazy(() => import('./components/AIKaliPage'));
const MarkMinerviniPage = lazy(() => import('./components/MarkMinerviniPage'));
const SpaceRPage = lazy(() => import('./components/SpaceRPage'));
const AISimulatorPage = lazy(() => import('./components/AISimulatorPage'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const StockIntelligence = lazy(() => import('./components/StockIntelligence'));
const ConnectPage = lazy(() => import('./components/ConnectPage'));
const StrategyLink = lazy(() => import('./components/StrategyLink'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#070b12]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full animate-spin"></div>
      <div className="text-sky-400 text-lg">Loading...</div>
    </div>
  </div>
);

// Set default axios timeout to 15 seconds for all requests
axios.defaults.timeout = 15000;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios response interceptor for better error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors and timeouts gracefully
    if (!error.response) {
      // Only log unique errors to avoid console spam
      const errorKey = error.message || 'unknown';
      if (!window._loggedErrors) window._loggedErrors = new Set();
      if (!window._loggedErrors.has(errorKey)) {
        window._loggedErrors.add(errorKey);
        console.warn('Network issue:', errorKey);
        // Clear after 30 seconds to allow re-logging
        setTimeout(() => window._loggedErrors?.delete(errorKey), 30000);
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    // Force re-render by updating state
    setTimeout(() => {
      window.location.href = '/dashboard'; // Use hard redirect to ensure state update
    }, 300);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/'; // Hard redirect to landing page
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <div className="App og-daylight">
      <Toaster position="top-right" richColors />
      <EmailVerificationBanner user={user} />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<Auth onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Strategy Magic Link - /s/:code for short shareable URLs */}
            <Route path="/s/:code" element={<StrategyLink />} />
            <Route
              path="/scanner"
              element={
                user ? (
                  <ScannerHome user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/scanner/:ticker"
              element={<AIScanner user={user} onLogout={handleLogout} />}
            />
            <Route
              path="/dashboard"
              element={
                user ? (
                  <MarketCommandCenter user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/builder"
              element={
                user ? (
                  <AIStrategyBuilder user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/strategies"
              element={
                user ? (
                  <SavedStrategies user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/pennystocks"
              element={<Navigate to="/gems" replace />}
            />
            <Route
              path="/gems"
              element={
                user ? (
                  <GemsTrading user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/gems-india"
              element={
                user ? (
                  <GemsIndiaTrading user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/nirvana"
              element={
                user ? (
                  <NirvanaTrading user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/minervini"
              element={
                user ? (
                  <MarkMinerviniPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/spacer"
              element={
                user ? (
                  <SpaceRPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/ai-simulator"
              element={
                user ? (
                  <AISimulatorPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/wheel"
              element={
                user ? (
                  <WheelStrategy user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/ai-tracker"
              element={
                user ? (
                  <AITrimbakPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/ai-labs"
              element={
                user ? (
                  <AITrimbakPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/kali"
              element={
                user ? (
                  <AIKaliPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/intelligence"
              element={
                user ? (
                  <StockIntelligence user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route
              path="/connect"
              element={
                user ? (
                  <ConnectPage user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            {/* Public pages */}
            <Route path="/pricing" element={<PricingPage user={user} onLogout={handleLogout} />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                (() => {
                  // Check for admin user from localStorage
                  const adminUser = localStorage.getItem('adminUser');
                  const token = localStorage.getItem('token');
                  if (adminUser && token) {
                    const parsedUser = JSON.parse(adminUser);
                    if (parsedUser.role === 'admin') {
                      return <AdminDashboard user={parsedUser} onLogout={handleLogout} />;
                    }
                  }
                  // Also check if regular user is admin
                  if (user && user.role === 'admin') {
                    return <AdminDashboard user={user} onLogout={handleLogout} />;
                  }
                  return <Navigate to="/admin/login" />;
                })()
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
    </GoogleOAuthProvider>
  );
}

export default App;
